// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { TypographyProps } from "@insite/mobius/Typography";
import { Dictionary } from "@insite/client-framework/Common/Types";

export const isWarthog = BLUEPRINT_NAME === "warthog";
export const isStoneage = BLUEPRINT_NAME === "stoneage";

export const constants = {
    header: {
        size: {
            sticky: 90,
        },
    },
    footer: {
        offsetTop: 96,
    },
    searchInput: {
        breakpoints: {
            lg: 1280,
        },
    },
    invoiceHistoryBuckets: {
        breakpoints: {
            md: 1375,
        },
    },
    marketoModalDefaultDimensions: {
        width: 700,
        height: 700,
    },
    cookies: {
        jettingAppSuggestJets: "jettingAppSuggestJets",
        jettingAppInitialize: "jettingAppInitialize",
        jettingAppAcceptingConfiguration: "jettingAppAcceptingConfiguration",
        productMediumDefaultImagePath: "productMediumDefaultImagePath",
        productLargeDefaultImagePath: "productLargeDefaultImagePath",
        productSmallDefaultImagePath: "productSmallDefaultImagePath",
        cloudinaryMediaRootPath: "cloudinaryMediaRootPath",
        cloudinaryGlobalNotFoundImagePath: "cloudinaryGlobalNotFoundImagePath",
        productPartNumber: "productPartNumber",
        dealerCategory: "dealerCategory",
        ptLanguage: "ptLanguage",
        jettingAppUnit: "JettingAppUnit",
    },
};

export const HEADER_HEIGHT_CSS_VAR = "--headerHeight";

export type MessageType = "Warning" | "Error";
export interface MessageVariant {
    icon?: string;
    color?: string;
    bgColor?: string;
}

export const messageVariants = {
    Warning: { icon: "IconWarning", bgColor: "#FDF9DE", color: "warning.main" },
    Error: { icon: "IconAlert", bgColor: "#FBDBE5", color: "danger.main" },
};

export const EMPTY_VALUE = "--";
export const DivWrapper = `div[class^="Wrapper"]`;

export const KEEP_ME_SIGNED_IN_STORAGE_KEYS = {
    lastShown: `xng_keepMeSignedIn_Modal_lastShown`,
} as const;

export const USA_ABBR = "US";

export const SLIDES_PER_VIEW_POINTS = {
    initial: -1,
    xs: 0,
    sm: 1,
    md: 2,
    lg: 3,
};

export const XNGAGE_WIDGET = "Xngage" as any;

export const paragraphStyles = {
    "as-h1": "As H1",
    "as-h2": "As H2",
    "as-h3": "As H3",
    "as-h4": "As H4",
    "as-h5": "As H5",
    "as-h6": "As H6",
    "special-h1": "Special H1",
    "special-h2": "Special H2",
    "as-blue": "Text Blue",
    "as-yellow": "Text Yellow",
    "as-orange": "Text Orange",
    "as-white": "Text White",
};

export const classToVariant: Dictionary<TypographyProps["variant"]> = {
    "as-h1": "h1",
    "as-h2": "h2",
    "as-h3": "h3",
    "as-h4": "h4",
    "as-h5": "h5",
    "as-h6": "h6",
};

export const customRichContentConfig = {
    extendedConfig: {
        paragraphStyles,
        paragraphMultipleStyles: true,
        paragraphFormat: {
            N: "Normal",
            H1: "Heading 1",
            H2: "Heading 2",
            H3: "Heading 3",
            H4: "Heading 4",
            H5: "Heading 5",
            H6: "Heading 6",
            PRE: "Code",
        },
    },
    collapsedToolbarButtons: {
        moreText: { buttons: ["paragraphFormat", "paragraphStyle", "textColor", "bold", "italic"] },
    },
};

export const fontConfig = {
    fontFamily: "'Roboto', sans-serif",
    weight: {
        regular: 400,
        medium: 500,
        bold: 700,
        black: 900,
    },
    headingColor: isWarthog ? "common.ui14" : "secondary.main",
    regular: {
        size: 16,
        lineHeight: "28px",
    },
    regularSingleLine: {
        size: 16,
        lineHeight: "19px",
    },
    small: {
        size: 14,
        lineHeight: "22px",
    },
    smallSingleLine: {
        size: 14,
        lineHeight: "16px",
    },
    xSmall: {
        size: 12,
        lineHeight: "18px",
    },
    xSmallSingleLine: {
        size: 12,
        lineHeight: "14px",
    },
    xxSmall: {
        size: 10,
        lineHeight: "15px",
    },
    xxSmallSingleLine: {
        size: 10,
        lineHeight: "11px",
    },
    h4: {
        size: 18,
        lineHeight: "21px",
    },
};

export const STONEAGE_OFFICE_LOCATION_TYPE = "StoneAge Office";

export const GOOGLE_MAPS_LIBRARIES: "places"[] = ["places"];
